import { buildCollection } from "@firecms/core";
import type { ContentStream } from "@one-ai/shared/cms/streams";
import { type Tag } from "@one-ai/shared/cms/schema";
import { NewTagAction } from "../components/NewTagAction";
import { RebuildActions } from "../components/RebuildActions";
import { StatusSwitchField } from "../fields/StatusSwitchField";
import type { CollectionStrings } from "../i18n/collection-strings";
import { onSaveRebuild } from "../lib/onSaveRebuild";
import { todayIso } from "./today";

/**
 * News tags.
 *
 * A collection rather than an enum in `schema.ts` — the way onelive models
 * `announcementTypes` — because publish-and-rebuild exists so an editor can add
 * a tag without a three-file code change and two deploys.
 *
 * **Ships empty on purpose.** There is no seed list here; marketing authors the
 * real tags. The comp's four categories (開發公告 / 合作案例 / 活動 /
 * Masa times) are a starting point for whoever does that, not a decision — each
 * still needs a slug and a Japanese label.
 */
export const tagCollection = (
    stream: ContentStream,
    { tag: t, streams }: CollectionStrings,
) =>
    buildCollection<Tag>({
        id: stream.tagCollection,
        path: stream.tagCollection,
        name: streams[stream.key].tag.collection.name,
        singularName: streams[stream.key].tag.collection.singularName,
        icon: "Label",
        description: streams[stream.key].tag.collection.description,
        // Order is the toolbar's, left to right: creating a tag is the thing an
        // editor came for; the rebuild buttons are a recovery path.
        Actions: [NewTagAction, RebuildActions],
        /**
         * The document id IS the tag slug — `3`, not `a7Kf9x2Qm`.
         *
         * That lets `WithId` hand the slug back for free, and makes tag slugs
         * permanent with no validation rule, because FireCMS cannot edit a
         * document id after creation.
         *
         * It is also what makes the reference picker on the news collection work:
         * a reference's last path segment is the slug the article stores.
         *
         * Since 2026-09-08 that id is **assigned, not typed**: `NewTagAction`
         * computes the next number, exactly as `NewArticleAction` does for an
         * article. `customId` stays `true` anyway — it is what makes the id the
         * slug, and it is still what a manually-reached `#new` form would demand.
         * Tags created before that date keep their written slug
         * (`product-update`): Firestore cannot rename a document, and those
         * numbers simply never enter the sequence.
         */
        customId: true,
        /**
         * **`create: false` is what hides FireCMS's own Add button**, leaving
         * `NewTagAction` as the only way in. Same policy block as
         * `articleCollection`, for the same two reasons: `canCreateEntity` also
         * gates **Copy entity**, which is the one other path that would ask an
         * editor to type a document id — and would ask it while copying a slug
         * that must not be reused. The action writes through
         * `context.dataSource`, which honours no permission, so suppressing the
         * button does not disarm the button that replaces it.
         */
        permissions: { read: true, create: false, edit: true, delete: true },
        /**
         * What a tag looks like in the news collection's reference picker.
         *
         * Without this FireCMS falls back to the first property, which is `date` —
         * so every tag chip in the picker read as "2026-09-03" and an editor could
         * not tell two tags apart. The slug itself is not addressable here (it is
         * the document id, not a property); the label is the more useful thing to
         * show anyway, and FireCMS prints the id alongside it.
         */
        previewProperties: ["label"],
        /**
         * The search bar, on both surfaces at once.
         *
         * This one flag is read by `EntityCollectionView` (the listing) and by
         * `ReferenceSelectionInner` (the picker an editor opens from a news
         * article's `tags` field), so there is no second switch for the second
         * place. What answers it is `lib/tagSearch.ts` — a controller of ours
         * rather than FireCMS's `localTextSearchEnabled`, whose index covers
         * top-level property keys only and so cannot see inside the `label`
         * map.
         *
         * It returns retired tags, which the listing view wants — un-retiring
         * one starts with finding it — and the news picker does not. What
         * separates the two is `lib/searchFilter.ts`, which puts back the
         * `forceFilter` the search branch drops; `news`'s `onPreSave` refuses a
         * retired tag regardless.
         */
        textSearchEnabled: true,
        callbacks: {
            /**
             * The document id IS the public URL segment — `/news/tag/<id>` — and
             * `customId: true` accepts whatever an editor types. FireCMS cannot
             * validate a custom id declaratively (`customId` is
             * `boolean | EnumValues | "optional"`, no regex), and a document id
             * cannot be edited afterwards, so a typo like "Product Update" would
             * become a permanent URL with a space and a capital in it.
             *
             * Throwing from `onPreSave` stops the save and shows the message in an
             * error snackbar. Same expression as `ArticleSchema.slug`, so the
             * CMS rejects what the read path would drop.
             *
             * **A backstop rather than the normal path since 2026-09-08.**
             * `NewTagAction` assigns the id before the form opens, and a plain
             * number passes this regex. What is left here is the create form
             * reached by hand at `/c/tags#new` — which `permissions.create:
             * false` hides but does not make unreachable — and that is exactly
             * the case where a typed id still needs validating.
             */
            onPreSave: ({ entityId, values }) => {
                if (entityId && !/^[a-z0-9-]+$/.test(entityId)) {
                    throw new Error(streams[stream.key].tag.invalidSlug(entityId));
                }
                /**
                 * `category` is stored explicitly even when the editor never
                 * touched the switch.
                 *
                 * `defaultValue` covers new entities only, so a tag written
                 * before this field existed reaches here as `undefined` — and
                 * the read side is content with that (the schema defaults it
                 * to `false`). What is not content with it is Firestore: a
                 * `where` clause skips a document missing the field, so those
                 * tags answer neither `category == true` nor
                 * `category == false` in the listing's column filter. Writing
                 * the value on every save backfills the collection as editors
                 * work through it, with no migration to run.
                 *
                 * `=== true` rather than `?? false` so the stored value is a
                 * boolean whatever arrived — the schema rejects anything else,
                 * and a rejected tag is a dropped card.
                 */
                return { ...values, category: values.category === true };
            },
            /**
             * A tag edit changes the public site as much as an article edit does:
             * the label renders as a chip on every article carrying it, and
             * retiring one removes its page and every link to it. So tags rebuild
             * on save too. See `lib/rebuild.ts`.
             */
            onSaveSuccess: onSaveRebuild,
        },
        properties: {
            date: {
                dataType: "string",
                name: t.fields.date.name,
                description: t.fields.date.description,
                defaultValue: todayIso(),
                validation: { required: true, matches: /^\d{4}-\d{2}-\d{2}$/ },
            },
            label: {
                dataType: "map",
                name: t.fields.label.name,
                description: t.fields.label.description,
                expanded: true,
                properties: {
                    zh: {
                        dataType: "string",
                        name: "繁體中文",
                        validation: { required: true },
                    },
                    jp: {
                        dataType: "string",
                        name: "日本語",
                        validation: { required: true },
                    },
                },
            },
            /**
             * Whether the tag is one of the news page's categories — the pills
             * in `TagFilterBar`.
             *
             * `tone: "positive"`, the opposite polarity from `retired` below:
             * `true` puts the tag in front of visitors. Its column filter in
             * the listing header is FireCMS's own, and it applies alongside a
             * search string thanks to the `matchesFilter` wrapper in
             * `App.tsx` — there is no filter UI of ours to add.
             *
             * `defaultValue: false` matches the schema's default, so a tag is a
             * category only by decision — but it applies to NEW entities only,
             * which is why there is no `required` here where `retired` has
             * one. Every tag written before 2026-09-10 stores no `category`,
             * and a required boolean over a missing value paints the field
             * with a validation error the moment an editor opens a tag they
             * have not touched. Nothing is wrong in that state: `undefined`
             * reads as "not a category" through the schema's default, and
             * `onPreSave` writes the explicit `false` on the way out.
             *
             * A `category == false` column filter still will not list a tag
             * that has never been saved since this field shipped — a Firestore
             * `where` skips a document missing the field — but one save of any
             * kind now settles it.
             */
            category: {
                dataType: "boolean",
                name: t.fields.category.name,
                description: t.fields.category.description,
                defaultValue: false,
                Field: StatusSwitchField,
                customProps: {
                    tone: "positive",
                    onLabel: t.fields.category.onLabel,
                    offLabel: t.fields.category.offLabel,
                },
            },
            retired: {
                dataType: "boolean",
                name: t.fields.retired.name,
                description: t.fields.retired.description,
                defaultValue: false,
                validation: { required: true },
                // Red when retired: `true` hides the tag everywhere public, which
                // is the opposite polarity from `news.published`.
                Field: StatusSwitchField,
                customProps: {
                    tone: "negative",
                    onLabel: t.fields.retired.onLabel,
                    offLabel: t.fields.retired.offLabel,
                },
            },
            order: {
                dataType: "number",
                name: t.fields.order.name,
                description: t.fields.order.description,
            },
        },
    });
